import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { PiBell, PiBellFill, PiCaretDown, PiUser } from 'react-icons/pi';
import { TbLogout2 } from 'react-icons/tb';
import { notificationService } from '../../services/notification.service';
import { authService } from '../../services/auth.service';
import { selectAuth } from '../../redux/slices/auth';
import { clearAuth } from '../../redux/slices/auth';
import styles from './TopBar.module.css';

const POLL_INTERVAL = 60_000;
const NOTIF_LIMIT = 10;

function formatNotifTime(dateStr) {
    return new Date(dateStr).toLocaleString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function NotificationBell() {
    const { t } = useTranslation('common');
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [isMarkingAll, setIsMarkingAll] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const fetchCount = async () => {
            try {
                const data = await notificationService.getUnreadCount();
                setUnreadCount(data.count ?? 0);
            } catch {}
        };
        fetchCount();
        const interval = setInterval(fetchCount, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const handle = (e) => {
            if (!wrapperRef.current?.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [isOpen]);

    const fetchNotifications = async (pageNum = 1) => {
        setIsLoading(true);
        try {
            const data = await notificationService.getNotifications({ page: pageNum, limit: NOTIF_LIMIT });
            setNotifications(prev => pageNum === 1 ? data.rows : [...prev, ...data.rows]);
            setTotal(data.total);
            setPage(pageNum);
        } catch {}
        finally { setIsLoading(false); }
    };

    const handleToggle = () => {
        if (!isOpen) fetchNotifications(1);
        setIsOpen(v => !v);
    };

    const handleNotifClick = async (notif) => {
        if (!notif.is_read) {
            try {
                await notificationService.markAsRead(notif.id);
                setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
                setUnreadCount(c => Math.max(0, c - 1));
            } catch {}
        }
        if (notif.link) {
            navigate(notif.link);
            setIsOpen(false);
        }
    };

    const handleMarkAll = async () => {
        setIsMarkingAll(true);
        try {
            await notificationService.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch {}
        finally { setIsMarkingAll(false); }
    };

    const hasMore = total > page * NOTIF_LIMIT;
    const hasUnread = notifications.some(n => !n.is_read);

    return (
        <div className={styles.bellWrapper} ref={wrapperRef}>
            <button className={styles.iconBtn} onClick={handleToggle} aria-label={t('notifications.title')}>
                {unreadCount > 0
                    ? <PiBellFill className={styles.bellIcon} />
                    : <PiBell className={styles.bellIcon} />}
                {unreadCount > 0 && (
                    <span className={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {isOpen && (
                <div className={styles.notifDropdown}>
                    <div className={styles.dropdownHeader}>
                        <span className={styles.dropdownTitle}>{t('notifications.title')}</span>
                        {hasUnread && (
                            <button
                                className={styles.markAllBtn}
                                onClick={handleMarkAll}
                                disabled={isMarkingAll}
                                type="button"
                            >
                                {t('notifications.mark_all_read')}
                            </button>
                        )}
                    </div>

                    <div className={styles.notifList}>
                        {isLoading && notifications.length === 0 ? (
                            <div className={styles.notifEmpty}>{t('loading', 'Loading...')}</div>
                        ) : notifications.length === 0 ? (
                            <div className={styles.notifEmpty}>{t('notifications.empty')}</div>
                        ) : (
                            <>
                                {notifications.map(notif => (
                                    <button
                                        key={notif.id}
                                        className={`${styles.notifItem} ${!notif.is_read ? styles.notifUnread : ''}`}
                                        onClick={() => handleNotifClick(notif)}
                                        type="button"
                                    >
                                        <div className={styles.notifDotSlot}>
                                            {!notif.is_read && <div className={styles.unreadDot} />}
                                        </div>
                                        <div className={styles.notifBody}>
                                            <span className={styles.notifTitle}>{notif.title}</span>
                                            {notif.message && (
                                                <span className={styles.notifMessage}>{notif.message}</span>
                                            )}
                                            <span className={styles.notifTime}>{formatNotifTime(notif.created_at)}</span>
                                        </div>
                                    </button>
                                ))}
                                {hasMore && (
                                    <button
                                        className={styles.showMoreBtn}
                                        onClick={() => fetchNotifications(page + 1)}
                                        disabled={isLoading}
                                        type="button"
                                    >
                                        {isLoading ? '...' : t('notifications.show_more')}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function UserMenu() {
    const { t } = useTranslation('common');
    const { user } = useSelector(selectAuth);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const handle = (e) => {
            if (!wrapperRef.current?.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [isOpen]);

    const handleLogout = async () => {
        try {
            await authService.logout();
            dispatch(clearAuth());
            navigate('/');
        } catch {}
    };

    const initials = [user?.first_name, user?.last_name]
        .filter(Boolean)
        .map(n => n[0])
        .join('')
        .toUpperCase() || '?';

    const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');

    return (
        <div className={styles.userMenuWrapper} ref={wrapperRef}>
            <button className={styles.userBtn} onClick={() => setIsOpen(v => !v)} type="button">
                <div className={styles.userAvatar}>{initials}</div>
                <span className={styles.userName}>{displayName}</span>
                <PiCaretDown className={`${styles.caretIcon} ${isOpen ? styles.caretOpen : ''}`} />
            </button>

            {isOpen && (
                <div className={styles.userDropdown}>
                    <button
                        className={styles.userDropdownItem}
                        onClick={() => { navigate('/crm/settings'); setIsOpen(false); }}
                        type="button"
                    >
                        <PiUser className={styles.dropItemIcon} />
                        {t('user_menu.profile')}
                    </button>
                    <div className={styles.dropdownDivider} />
                    <button
                        className={`${styles.userDropdownItem} ${styles.logoutItem}`}
                        onClick={handleLogout}
                        type="button"
                    >
                        <TbLogout2 className={styles.dropItemIcon} />
                        {t('user_menu.logout')}
                    </button>
                </div>
            )}
        </div>
    );
}

function TopBar() {
    return (
        <div className={styles.topBar}>
            <div className={styles.topBarLeft} />
            <div className={styles.topBarRight}>
                <NotificationBell />
                <UserMenu />
            </div>
        </div>
    );
}

export default TopBar;
